import { AxiosError } from "axios";
import queryString from "query-string";
import { ThunkDispatch } from "redux-thunk";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { determineApiMessages } from "@utils/apiMessages";
import { getBaseStore } from "@utils/configuration/aem";
import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { CheckoutSteps, DeliveryTypes } from "@utils/enums";
import { attemptMethod } from "@utils/general";
import { MaybeType } from "@utils/maybe";

import CallService, { CallServiceKey } from "@services/call.service";
import { PreferredStoreService } from "@services/preferredStore.service";
import {
    trackAddToCartImpression,
    trackRemoveFromCartImpression,
    trackUpdateCartItemImpression,
} from "@services/tracking/trackers/updateCartItemImpressionTracker";

import CreateApiModule from "@redux/create-api-module";
import {
    broadcastMessage,
    clearMessagesExcludingIds,
} from "@redux/modules/value/messages/messages.utils";
import {
    IState,
    TActionOptions,
    TApiAction,
    TExtraThunkArgs,
} from "@redux/store.types";
import { getApiDataState } from "@redux/utils";

import { ECommerceModel } from "@components/Product/ECommerce/eCommerce.types";

import { CartItemModel, CartModel } from "./@types/cart.model";
import { DISCRIMINATORS } from "./cart.discriminators";
import { returnMaybeCartModel } from "./cart.guard";
import {
    recalculateRemovableQuantity,
    validateAndRedirect,
} from "./cart.helpers";
import { CartMapper } from "./cart.mapper";
import CartReducer from "./cart.reducer";
import { getCart } from "./cart.selectors";
import {
    $CartModuleName,
    AddToCartBulkRequestBody,
    AddToCartRequestBody,
    CartItemConfigUpdateOrigin,
    CheckoutStepValidationResult,
} from "./cart.types";

const { actions, constants } = CreateApiModule(
    $CartModuleName,
    ({ url }) => url,
    {
        reducer: CartReducer,
        mapper: CartMapper,
        transformResponse: (data?: any) => {
            const mayBeCartModel = returnMaybeCartModel(data);
            if (mayBeCartModel.type === MaybeType.Just) {
                manageApiMessages(mayBeCartModel.value);
                manageSalesIncentives(mayBeCartModel.value);
            }
            return data;
        },
    },
);

export const clear = actions.clear;
export const refetch = actions.refetch;

const checkCartCall = (action?: TApiAction) => {
    if (action?.type === constants.SUCCESS) {
        if (action.payload && (action.payload as CartModel).items.length > 0) {
            CallService.enable(CallServiceKey.Cart);
        } else {
            CallService.disable(CallServiceKey.Cart);
        }
    } else {
        // always enable on error
        CallService.enable(CallServiceKey.Cart);
    }
};

// explicit code path for CTA.CHANGE_STORE_AND_CNC
export const updateStoreAndDeliveryType =
    (
        deliveryType: string,
        shop: string | null,
        sku: string,
        force: boolean = false,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items`,
                        bff: true,
                    },
                    data: {
                        deliveryType,
                        quantity: 0, // don't increase product count
                        shopId:
                            deliveryType === DeliveryTypes.ClickAndCollect &&
                            shop
                                ? shop
                                : null,
                        sku,
                    },
                },
                "POST",
                force,
            ),
        );

export const addToCart =
    (
        {
            deliveryType,
            shopId,
            quantity,
            sku,
            additionalServices,
        }: AddToCartRequestBody,
        productObject: ECommerceModel | ProductTileModel,
        force: boolean = false,
        productSubType?: string,
        extraConfiguration?: Record<string, unknown>,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items${
                            productSubType ? `/${productSubType}` : ""
                        }`,
                        bff: true,
                    },
                    data: {
                        ...(extraConfiguration ?? {}),
                        deliveryType: deliveryType,
                        sku: sku,
                        quantity: quantity,
                        additionalServices: additionalServices,
                        shopId:
                            deliveryType === DeliveryTypes.ClickAndCollect &&
                            !!shopId
                                ? shopId
                                : null,
                    },
                },
                "POST",
                force,
            ),
        );

        if (action.type === constants.SUCCESS && productObject) {
            trackAddToCartImpression(productObject, quantity);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const addToCartBulk =
    (
        addToCartBulk: AddToCartBulkRequestBody,
        trackingProducts: Array<{
            product?: ProductTileModel;
            quantity: number;
        }>,
        force: boolean = false,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items-bulk`,
                        bff: true,
                    },
                    data: addToCartBulk,
                },
                "POST",
                force,
            ),
        );

        if (action.type === constants.SUCCESS && trackingProducts.length > 0) {
            trackingProducts.forEach(
                ({ product, quantity }) =>
                    product && trackAddToCartImpression(product, quantity),
            );
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const removeFreeArticleFromCart =
    (id: string, productJson: CartItemModel, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/cta/removefreearticle/${id}`,
                        bff: true,
                    },
                },
                "PATCH",
                force,
            ),
        );

        if (action.type === constants.SUCCESS && productJson) {
            trackRemoveFromCartImpression(productJson, productJson.quantity);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const addConfigurableProductToCart =
    (data: any, endPoint: string) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/cartservice/${getBaseStore()}${endPoint}`,
                    },
                    data: data,
                },
                "POST",
                true,
                { mergeParams: false },
            ),
        );

        if (action.type === constants.SUCCESS) {
            // todo add to cart, to be determined after backend implementation
            // trackAddToCartImpression(productObject, quantity);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const removeFromCart =
    (id: string, quantityToRemove: number, free: boolean) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        const cart = getCart(getState())!;

        const productJson = cart.items.find(item => item.id === id);

        const newQuantity = recalculateRemovableQuantity(
            cart,
            id,
            quantityToRemove,
        );

        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items/${id}`,
                        bff: true,
                    },
                    data: {
                        quantityToRemove: newQuantity,
                        free,
                    },
                },
                "DELETE",
                true,
            ),
        );

        if (action.type === constants.SUCCESS && productJson) {
            trackRemoveFromCartImpression(productJson, quantityToRemove);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const changeStore =
    (store: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/cta/changestore`,
                        bff: true,
                    },
                    data: { store },
                },
                "PATCH",
                force,
            ),
        );

export const changeStoreToECorner =
    (force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/cta/changestore/ecorner`,
                        bff: true,
                    },
                },
                "PATCH",
                force,
            ),
        );

export const updateCartItem =
    (
        id: string,
        quantity: number,
        deliveryType: string,
        product?: CartItemModel,
        quantityDelta?: number,
        additionalServices?: Array<string>,
        actionOptions: TActionOptions = { mergeParams: true },
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items/${id}`,
                        bff: true,
                    },
                    data: {
                        quantity,
                        deliveryType,
                        additionalServices,
                    },
                },
                "PATCH",
                true,
                actionOptions,
            ),
        );

        if (action.type === constants.SUCCESS && product) {
            trackUpdateCartItemImpression(product, quantityDelta);
        }

        checkCartCall(action);

        return Promise.resolve(action);
    };

export const updateCartItemConfig =
    (
        id: string,
        quantity: number,
        deliveryType: string,
        config: Record<string, unknown>,
        product: CartItemModel,
        productSubType: string,
        origin: CartItemConfigUpdateOrigin,
        shopId?: string,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const action = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items/${productSubType}/${id}/${origin}`,
                        bff: true,
                    },
                    data: {
                        ...config,
                        shopId,
                        deliveryType,
                        quantity,
                        sku: product.sku,
                    },
                },
                "PATCH",
                true,
            ),
        );

        return Promise.resolve(action);
    };

export const checkout =
    (
        shopId: string,
        conditionMinAgeChecked: boolean,
        conditionPartnerEmailChecked: boolean,
        bonusCardNumber?: string,
        deliveryAddressId?: string,
        billingAddressId?: string,
        acceptedAdditionalServiceDocumentCodes: Array<string> = [],
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/checkout`,
                        bff: true,
                    },
                    data: {
                        deliveryAddressId,
                        billingAddressId,
                        shopId,
                        conditionMinAgeChecked,
                        conditionPartnerEmailChecked,
                        acceptedAdditionalServiceDocumentCodes,
                        ...(bonusCardNumber ? { bonusCardNumber } : {}),
                    },
                },
                "POST",
                true,
            ),
        );
    };

export const addPromotionCode =
    (code: string, bonusCardNumber?: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/promotioncodes`,
                        bff: true,
                    },
                    data: {
                        code,
                        ...(bonusCardNumber ? { bonusCardNumber } : {}),
                    },
                },
                "POST",
                force,
                {
                    discriminator: DISCRIMINATORS.PromotionCode,
                },
            ),
        );

export const removePromotionCode =
    (code: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/promotioncodes/${code}`,
                        bff: true,
                    },
                },
                "DELETE",
                force,
                {
                    discriminator: DISCRIMINATORS.PromotionCode,
                },
            ),
        );

export const addCoupon =
    (
        requestData: {
            code: string;
            recaptcha: string;
            bonusCardNumber?: string | null;
        },
        force: boolean = false,
    ) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/coupons`,
                        bff: true,
                    },
                    data: {
                        ...requestData,
                        ...(requestData.bonusCardNumber
                            ? { bonusCardNumber: requestData.bonusCardNumber }
                            : {}),
                    },
                },
                "POST",
                force,
                {
                    skipErrors: [404],
                },
            ),
        );
    };

export const removeCoupon =
    (cardCode: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/coupons/${cardCode}`,
                        bff: true,
                    },
                },
                "DELETE",
                force,
            ),
        );
    };

export const acceptNegativeDeliveryPromise =
    (sku: string | undefined, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/cta/acceptnegativedeliverypromise`,
                        bff: true,
                    },
                    data: { sku },
                },
                "PATCH",
                force,
            ),
        );

export const acceptPositiveDeliveryPromise =
    (sku: string, force: boolean = false) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/cta/acceptpositivedeliverypromise`,
                        bff: true,
                    },
                    data: { sku },
                },
                "PATCH",
                force,
            ),
        );

export const load =
    (force: boolean = false) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
        getState: () => IState,
    ) => {
        if (
            !CallService.shouldCall(CallServiceKey.Cart, { days: 30 }) &&
            !force
        ) {
            return {
                type: constants.SUCCESS,
                payload: getApiDataState(getState(), $CartModuleName)?.data,
                actionOptions: {
                    discriminator: DISCRIMINATORS.Load,
                    skipErrors: [400],
                },
            } as TApiAction;
        }

        const parsedQueryString = queryString.parse(window.location.search);

        const abandonedCartData = attemptMethod(() => {
            const data = parsedQueryString?.["abandonedCartData"] as
                | string
                | null;
            if (!data) {
                return undefined;
            }

            return JSON.parse(atob(data));
        }, undefined);

        let loadResult;

        if (isPageTemplate(PageTemplates.SHOPPING_CART) && abandonedCartData) {
            loadResult = await dispatch(
                actions.load(
                    {
                        path: {
                            url: `/${getBaseStore()}/carts/abandonedCart`,
                            bff: true,
                        },
                        data: abandonedCartData,
                    },
                    "POST",
                    force,
                    {
                        discriminator: DISCRIMINATORS.Load,
                        skipErrors: [400],
                    },
                ),
            );
            const { abandonedCartData: _, ...rest } = parsedQueryString;
            const stringifiedQueryParams = attemptMethod(
                () => queryString.stringify(rest),
                "",
            );
            window.history.pushState(null, "", `?${stringifiedQueryParams}`);
        } else {
            loadResult = await dispatch(
                actions.load(
                    {
                        path: {
                            url: `/${getBaseStore()}/carts`,
                            bff: true,
                        },
                    },
                    "GET",
                    force,
                    {
                        discriminator: DISCRIMINATORS.Load,
                        skipErrors: [400],
                    },
                ),
            );
        }

        if (loadResult?.payload?.storeId) {
            PreferredStoreService.setLastKnownCartStoreId(
                loadResult.payload.storeId,
            );
        } else if (loadResult?.payload?.error) {
            PreferredStoreService.removeLastKnownCartStoreId();
        }

        checkCartCall(loadResult);

        return Promise.resolve(loadResult);
    };

export const startCheckout =
    () =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        return await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts`,
                        bff: true,
                    },
                },
                "GET",
                true,
                {
                    discriminator: DISCRIMINATORS.StartCheckout,
                    skipErrors: [400],
                },
            ),
        );
    };

export type GiftCardRequest = {
    sku: string;
    quantity: number;
    amountInEuros: number;
    personalMessage: string;
};

export const addGiftCardToCart =
    (request: GiftCardRequest) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) => {
        const loadResult = await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items/giftcards`,
                        bff: true,
                    },
                    data: request,
                },
                "POST",
                true,
            ),
        );

        checkCartCall(loadResult);

        return loadResult;
    };

export const updateGiftCardInCart =
    (id: string, request: GiftCardRequest) =>
    async (dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>) =>
        await dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts/items/giftcards/${id}`,
                        bff: true,
                    },
                    data: request,
                },
                "PATCH",
                true,
            ),
        );

export const validateCheckoutStep =
    (checkoutStep: CheckoutSteps, force: boolean = false) =>
    async (
        dispatch: ThunkDispatch<IState, TExtraThunkArgs, TApiAction>,
    ): Promise<CheckoutStepValidationResult> =>
        dispatch(
            actions.load(
                {
                    path: {
                        url: `/${getBaseStore()}/carts`,
                        bff: true,
                    },
                },
                "GET",
                force,
                {
                    discriminator: DISCRIMINATORS.Load,
                },
            ),
        ).then(loadResult => {
            checkCartCall(loadResult);

            const payload = loadResult.payload;

            const cart = (
                (payload as { error: AxiosError })?.error ? undefined : payload
            ) as CartModel | undefined;

            return validateAndRedirect(checkoutStep)(cart);
        });

const manageSalesIncentives = (shoppingCart: CartModel) => {
    if (shoppingCart.salesIncentives.length > 0) {
        broadcastMessage({
            id: "sales-incentive",
            status: "primary",
            text: shoppingCart.salesIncentives,
            dismissible: false,
        });
    }
};

const manageApiMessages = (shoppingCart: CartModel) => {
    clearMessagesExcludingIds([
        "general-cart",
        "carts-merged",
        "payment-error-message",
        "ecorner-public",
        "update-giftcard-error",
        "sale-incentive",
    ]);

    const itemHasMessage = shoppingCart.items.filter(
        item => item.messages !== null,
    );

    let mergedMessages = [];

    if (shoppingCart.messages.length > 0 || itemHasMessage.length > 0) {
        mergedMessages.push(...shoppingCart.messages);
        determineApiMessages(mergedMessages, shoppingCart.items);
    }
};

export const APIStatus = constants;
